import AxiosInstance from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { getItem, setItem, removeItem, clear } from "../../utils/storage";

const getToken = () => {
  const tokens = ["adminToken", "workerToken", "customerToken", "donorToken"];
  for (const token of tokens) {
    const value = getItem(token);
    if (value) return value;
  }
  return null;
};

let isToastDisplayed = false;

// Cache for handleAuth results to prevent repeated API calls
// Cache persists until token changes (no time-based expiry)
const authCache = {
  data: null,
  token: null,
};

// JOB-3251: in-flight request dedupe. When many components mount in the same
// tick, each hits handleAuth with an empty cache and fires its own request.
// Share the pending promise so they all resolve from one API call.
const inFlight = {
  token: null,
  promise: null,
};

// Internal function to clear cache when token changes
const clearAuthCacheInternal = () => {
  authCache.data = null;
  authCache.token = null;
  inFlight.token = null;
  inFlight.promise = null;
};

const forceLogout = (
  navigate,
  message = "Session expired. Please log in again."
) => {
  // Check if user is impersonating - if so, return to super admin instead of logging out
  const isImpersonated = getItem("isImpersonated") === "true";
  const oldSession = getItem("oldSession");
  const oldAdminId = getItem("oldAdminId");
  const appUrl = getItem("appUrl"); // Get APP_URL from env (stored during sign-in)

  if (isImpersonated && oldSession) {
    // Clear company-related data first
    removeItem("CompanyId");
    removeItem("CompanyUrl");
    removeItem("isImpersonated");
    removeItem("impersonatedBy");
    removeItem("CustomerId");
    removeItem("currentLogoPath");
    removeItem("oldSession");
    removeItem("oldAdminId");
    removeItem("appUrl");
    localStorage.removeItem("companyName");

    const navigationPath = "/superadmin/company";

    // Cross-origin restore (different `appUrl` than current host) genuinely
    // requires a real navigation: storage doesn't follow across origins, so
    // we encode the session into a hash for restoreAuthFromHash() to pick up.
    const sameOrigin =
      !appUrl ||
      (typeof window !== "undefined" &&
        appUrl.startsWith(window.location.origin));

    if (sameOrigin) {
      // Same origin → restore the super-admin session in storage directly and
      // do an SPA navigation. No full reload needed.
      setItem("adminToken", oldSession);
      if (oldAdminId) {
        setItem("admin_id", oldAdminId);
      }
      if (navigate) {
        navigate(navigationPath, { replace: true });
      } else if (typeof window !== "undefined") {
        window.history.pushState({}, "", navigationPath);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
      return;
    }

    // Cross-origin: must do a full navigation so the target host sees the
    // hash-encoded session and runs restoreAuthFromHash() on its own boot.
    const superAdminData = {
      adminToken: oldSession,
      adminId: oldAdminId || null,
      appUrl,
    };
    const encodedData = btoa(JSON.stringify(superAdminData));
    window.location.href = `${appUrl}${navigationPath}#auth=${encodedData}`;
    return;
  }

  // Normal logout - clear everything
  if (!isToastDisplayed) {
    toast.error(message,{
      toastId: "auth-logout-toast", // Unique ID prevents duplicates
    });
    isToastDisplayed = true;
  }
  // Clear auth cache on logout
  clearAuthCacheInternal();
  clear();
  navigate("/auth/login", { state: { error: message } });
};

const handleAuth = async (navigate, location, redirectPath = "/auth/login") => {
  // Check if impersonating - if so, use customerToken
  const isImpersonated = getItem("isImpersonated") === "true";
  let token = getToken();

  // Check cache first - return cached data if token hasn't changed
  // No time-based expiry - cache persists until token changes
  // Note: Redirects and storage updates happen in first call only
  // Subsequent calls with same token return cached data (redirects already handled)
  if (authCache.data && authCache.token === token) {
    return authCache.data;
  }

  // Clear cache if token changed
  if (authCache.token !== token) {
    clearAuthCacheInternal();
  }

  // JOB-3251: if a request for this token is already in flight, reuse it.
  // Prevents the "token_data storm" when many components mount simultaneously.
  if (inFlight.promise && inFlight.token === token) {
    return inFlight.promise;
  }

  // If impersonating and no token found, try to restore super admin session
  if (!token && isImpersonated) {
    const oldSession = getItem("oldSession");
    const oldAdminId = getItem("oldAdminId");
    if (oldSession) {
      setItem("adminToken", oldSession);
      if (oldAdminId) {
        setItem("admin_id", oldAdminId);
      }
      removeItem("oldSession");
      removeItem("oldAdminId");
      removeItem("isImpersonated");
      removeItem("impersonatedBy");
      if (navigate) {
        navigate("/superadmin/index", { replace: true });
      } else if (typeof window !== "undefined") {
        // No router navigator available (rare — e.g. invoked outside a React
        // render). Push history state so React Router still picks it up
        // without a full page reload.
        window.history.pushState({}, "", "/superadmin/index");
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
      return;
    }
  }

  if (!token) {
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
    console.error("[Auth] Token not found – redirecting to login.", {
      currentPath,
      message: "Token not found. Please log in.",
    });
    forceLogout(navigate, "Token not found. Please log in.");
    return;
  }

  // JOB-3251: register this in-flight promise so concurrent callers reuse it.
  inFlight.token = token;
  inFlight.promise = (async () => {
  try {
    const res = await AxiosInstance.post(`/v1/auth/token_data`, { token });

    const statusOk =
      res?.data?.statusCode === "200" || res?.data?.statusCode === 200;
    if (!statusOk) {
      forceLogout(navigate, "Invalid token or unauthorized access");
      return;
    }

    const {
      Role,
      AdminId,
      CustomerId,
      CompanyId,
      CompanyUrl,
      WorkerId,
      IsPlanActive,
      UserId,
      Gateway,
      IsImpersonated,
    } = res?.data?.data;

    // Check if impersonating and trying to access superadmin routes - redirect to company dashboard
    if (isImpersonated || IsImpersonated) {
      if (window.location.pathname.includes("/superadmin")) {
        const companyUrl = CompanyUrl || getItem("CompanyUrl");
        if (companyUrl && navigate) {
          navigate(`/${companyUrl}/index`, { replace: true });
          return;
        }
      }
    }

    const state = { Role, id: null, navigats: [] };
    switch (Role) {
      case "Admin":
        // Only allow Admin role if NOT impersonating
        if (!isImpersonated && !IsImpersonated) {
          if (!window.location.pathname.includes("/superadmin")) {
            setItem("admin_id", AdminId);
            state.redirect = "/superadmin/index";
            state.navigats = ["/index"];
          }
        } else {
          // Impersonating - redirect to company dashboard
          const companyUrl = CompanyUrl || getItem("CompanyUrl");
          if (companyUrl && navigate) {
            navigate(`/${companyUrl}/index`, { replace: true });
            return;
          }
        }
        break;

      case "Customer":
        if (IsPlanActive) {
          if (!window.location.pathname.includes(`/${CompanyUrl}/customers`)) {
            setItem("CustomerId", UserId);
            state.redirect = `/${CompanyUrl}/customers/index`;
            state.navigats = ["/index"];
          }
        } else if (!IsPlanActive) {
          forceLogout(navigate, "Company don't have any active plans");
        }
        break;

      case "Company":
        setItem("CompanyId", CompanyId);
        if (IsPlanActive) {
          if (!window.location.pathname.includes(`/${CompanyUrl}`)) {
            setItem("CompanyId", CompanyId);
            state.redirect = `/${CompanyUrl}/index`;
            state.navigats = ["/index"];
          }
        } else if (
          !IsPlanActive &&
          !location?.pathname.includes("/plan-purchase")
        ) {
          if (!location.state?.navigats?.includes("/plan")) {
            state.redirect = `/${CompanyUrl}/plan`;
            state.navigats = ["/index", "/plan"];
            navigate(`/plan`);
          }
        }

        break;

      case "Worker":
        if (IsPlanActive) {
          if (
            !window.location.pathname.includes(`/${CompanyUrl}/staff-member`)
          ) {
            setItem("worker_id", UserId);
            state.redirect = `/${CompanyUrl}/staff-member/index`;
            state.navigats = ["/index"];
          }
        } else if (!IsPlanActive) {
          forceLogout(navigate, "Company don't have any active plans");
        }
        break;
      case "Donor":
        if (IsPlanActive) {
          if (!window.location.pathname.includes(`/${CompanyUrl}/donors`)) {
            setItem("DonorId", UserId);
            state.redirect = `/${CompanyUrl}/donors/index`;
            state.navigats = ["/index"];
          }
        } else if (!IsPlanActive) {
          forceLogout(navigate, "Company don't have any active plans");
        }
        break;
      default:
        console.error("Unrecognized Role");
        forceLogout(navigate, "Unrecognized role. Please log in again.");
        return;
    }

    // Cache the successful response (no timestamp needed - cache until token changes)
    authCache.data = res?.data;
    authCache.token = token;

    return res?.data;
  } catch (error) {
    // Clear cache on error
    clearAuthCacheInternal();
    console.error("Error:", error?.message);

    // If impersonating and there's an error, try to restore super admin session
    if (isImpersonated) {
      const oldSession = getItem("oldSession");
      const oldAdminId = getItem("oldAdminId");
      if (oldSession) {
        setItem("adminToken", oldSession);
        if (oldAdminId) {
          setItem("admin_id", oldAdminId);
        }
        removeItem("oldSession");
        removeItem("oldAdminId");
        removeItem("isImpersonated");
        removeItem("impersonatedBy");
        removeItem("CompanyId");
        removeItem("CompanyUrl");
        if (navigate) {
          navigate("/superadmin/index", { replace: true });
        } else if (typeof window !== "undefined") {
          // No router navigator available — push history state so the change
          // routes through React Router instead of triggering a full reload.
          window.history.pushState({}, "", "/superadmin/index");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
        return;
      }
    }

    if (error?.response?.status === 401) {
      forceLogout(navigate, "Session expired. Please log in again.");
    } else if (error?.response?.status === 400) {
      forceLogout(navigate, error?.response?.data?.message);
    } else {
      forceLogout(navigate, "An unexpected error occurred.");
    }
  }
  })();
  try {
    return await inFlight.promise;
  } finally {
    // Whether success or failure, release the in-flight slot so the next caller
    // either hits the populated cache (success) or fires a fresh request (failure).
    if (inFlight.token === token) {
      inFlight.promise = null;
      inFlight.token = null;
    }
  }
};
// Export function to clear cache (useful for logout or token refresh)
export const clearAuthCache = () => {
  clearAuthCacheInternal();
};

export { handleAuth };
